import React, { useState, useEffect, useMemo } from 'react';
import { scheduledReportsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Loader2, CalendarClock, X, Clock, Mail, Info } from 'lucide-react';
import { toast } from 'sonner';

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'biannual', label: 'Biannual (6 mo)' },
  { value: 'annual', label: 'Annual' },
];

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const DATE_RANGE_LABELS = {
  last_7_days: 'Last 7 days',
  last_30_days: 'Last 30 days',
  this_month: 'This month',
  last_month: 'Last month',
  custom: 'Custom dates',
};

export function ScheduleReportModal({ open, onOpenChange, reportType, reportName, currentFilters, dateRangeType, existingSchedule, onCreated }) {
  const { tenant } = useAuth();

  const isEdit = !!existingSchedule;
  const [frequency, setFrequency] = useState('weekly');
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [sendTime, setSendTime] = useState('07:00');
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState([]);
  const [saving, setSaving] = useState(false);

  // Initialize from existing schedule when editing
  useEffect(() => {
    if (existingSchedule) {
      setFrequency(existingSchedule.frequency_type);
      setDayOfWeek(existingSchedule.frequency_day_of_week ?? 0);
      setDayOfMonth(existingSchedule.frequency_day_of_month ?? 1);
      setSendTime(existingSchedule.send_time_local || '07:00');
      setEmails(existingSchedule.recipient_emails || []);
    } else {
      setFrequency('weekly');
      setDayOfWeek(0);
      setDayOfMonth(1);
      setSendTime('07:00');
      setEmails([]);
    }
  }, [existingSchedule, open]);

  const needsWeekday = frequency === 'weekly' || frequency === 'biweekly';
  const needsDayOfMonth = ['monthly', 'quarterly', 'biannual', 'annual'].includes(frequency);

  const addEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;
    const parts = trimmed.split(/[,;\s]+/).filter(Boolean);
    const valid = [];
    for (const p of parts) {
      if (!EMAIL_RE.test(p)) {
        toast.error(`Invalid email: ${p}`);
        return;
      }
      if (!emails.includes(p)) valid.push(p);
    }
    if (valid.length) setEmails(prev => [...prev, ...valid]);
    setEmailInput('');
  };

  const removeEmail = (email) => setEmails(prev => prev.filter(e => e !== email));

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail();
    }
  };

  // Compute next run preview
  const previewInfo = useMemo(() => {
    const tz = tenant?.timezone || 'America/Chicago';
    const rangeLabel = DATE_RANGE_LABELS[dateRangeType] || dateRangeType || 'Last 30 days';
    const freqLabel = FREQUENCIES.find(f => f.value === frequency)?.label || frequency;
    let dayLabel = '';
    if (needsWeekday) dayLabel = ` on ${WEEKDAYS[dayOfWeek]}s`;
    if (needsDayOfMonth) dayLabel = ` on day ${dayOfMonth}`;
    return { freqLabel, dayLabel, rangeLabel, tz, time: sendTime };
  }, [frequency, dayOfWeek, dayOfMonth, sendTime, dateRangeType, tenant, needsWeekday, needsDayOfMonth]);

  const handleSubmit = async () => {
    if (emails.length === 0) {
      toast.error('Add at least one recipient email');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        report_type: reportType,
        report_name: reportName,
        report_config_json: {
          date_range_type: dateRangeType || 'last_30_days',
          filters: currentFilters || {},
        },
        frequency_type: frequency,
        frequency_day_of_week: needsWeekday ? dayOfWeek : null,
        frequency_day_of_month: needsDayOfMonth ? dayOfMonth : null,
        send_time_local: sendTime,
        recipient_emails: emails,
      };

      if (isEdit) {
        const { data } = await scheduledReportsAPI.update(existingSchedule.id, {
          report_config_json: payload.report_config_json,
          frequency_type: payload.frequency_type,
          frequency_day_of_week: payload.frequency_day_of_week,
          frequency_day_of_month: payload.frequency_day_of_month,
          send_time_local: payload.send_time_local,
          recipient_emails: payload.recipient_emails,
        });
        toast.success('Schedule updated');
        onCreated?.(data);
      } else {
        const { data } = await scheduledReportsAPI.create(payload);
        toast.success('Report scheduled');
        onCreated?.(data);
      }
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-primary" />
            {isEdit ? 'Edit Schedule' : 'Schedule Report'}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update the schedule for this report.' : `Set up automatic delivery for "${reportName}".`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Report info */}
          <div className="p-3 rounded-lg bg-muted/50 text-sm space-y-1">
            <p className="font-medium">{reportName}</p>
            <p className="text-muted-foreground">Date range: {previewInfo.rangeLabel} (computed at run time)</p>
            {currentFilters && Object.keys(currentFilters).filter(k => currentFilters[k]).length > 0 && (
              <p className="text-muted-foreground">{Object.keys(currentFilters).filter(k => currentFilters[k]).length} filter(s) applied</p>
            )}
          </div>

          {/* Frequency */}
          <div className="space-y-2">
            <Label>Frequency</Label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger data-testid="schedule-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FREQUENCIES.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Day selector */}
          {needsWeekday && (
            <div className="space-y-2">
              <Label>Day of week</Label>
              <Select value={String(dayOfWeek)} onValueChange={v => setDayOfWeek(Number(v))}>
                <SelectTrigger data-testid="schedule-day-of-week"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {needsDayOfMonth && (
            <div className="space-y-2">
              <Label>Day of month</Label>
              <Select value={String(dayOfMonth)} onValueChange={v => setDayOfMonth(Number(v))}>
                <SelectTrigger data-testid="schedule-day-of-month"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                    <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Info className="w-3 h-3" />If the month has fewer days, we send on the last day.
              </p>
            </div>
          )}

          {/* Time */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />Send time ({previewInfo.tz})
            </Label>
            <Input type="time" value={sendTime} onChange={e => setSendTime(e.target.value)} data-testid="schedule-send-time" />
          </div>

          {/* Recipients */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" />Recipients
            </Label>
            <div className="flex gap-2">
              <Input
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={addEmail}
                placeholder="email@example.com"
                data-testid="schedule-email-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={addEmail} className="shrink-0">Add</Button>
            </div>
            {emails.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {emails.map(e => (
                  <Badge key={e} variant="secondary" className="gap-1 pr-1">
                    {e}
                    <button onClick={() => removeEmail(e)} className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            {emails.length === 0 && <p className="text-xs text-destructive">At least one recipient is required</p>}
          </div>

          {/* Preview panel */}
          <div className="rounded-lg border bg-card p-4 space-y-2">
            <p className="text-sm font-semibold flex items-center gap-1.5"><CalendarClock className="w-4 h-4 text-primary" />Schedule Preview</p>
            <div className="text-sm space-y-1 text-muted-foreground">
              <p>Runs <strong className="text-foreground">{previewInfo.freqLabel}</strong>{previewInfo.dayLabel} at <strong className="text-foreground">{previewInfo.time}</strong> ({previewInfo.tz})</p>
              <p>Date range: <strong className="text-foreground">{previewInfo.rangeLabel}</strong> (computed at run time)</p>
              <p>Recipients: <strong className="text-foreground">{emails.length > 0 ? emails.join(', ') : 'None'}</strong></p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving || emails.length === 0} data-testid="schedule-submit-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? 'Update Schedule' : 'Create Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
